/**
 * Reads a required environment variable, logging an error if it is missing or
 * empty. Returns an empty string as a fallback so callers keep a `string` type.
 */
export const requireEnv = (key: string, value: string | undefined): string => {
  if (!value) {
    console.error(`[PatientPortal] Missing environment variable: ${key}`);
  }
  return value ?? '';
};

export const getAmplarFeatureHighlightsContentfulId = () =>
  import.meta.env.VITE_FEATURE_HIGHLIGHTS_CONTENTFUL_ID_AMPLAR;

export const getMyHealthFeatureHighlightsContentfulId = () =>
  import.meta.env.VITE_FEATURE_HIGHLIGHTS_CONTENTFUL_ID_MY_HEALTH;

export const isPatientPortalDeveloperMode = () =>
  import.meta.env.VITE_TOGGLE_DEVELOPER_FEATURE === 'true';

export const isAccountDeletionDisabled = () =>
  import.meta.env.VITE_DISABLE_ACCOUNT_DELETION === 'true';

export const isOnboardingDisabled = () =>
  import.meta.env.VITE_DISABLE_ONBOARDING === 'true';

export const isMockAppointmentIframeEnabled = () =>
  import.meta.env.VITE_ENABLE_MOCK_APPOINTMENT_IFRAME === 'true';

/**
 * Returns the origins permitted as the appointment iframe `src` and as the
 * `event.origin` for inbound `postMessage` events, keyed by the patient portal
 * system that owns them.
 *
 * Configured via `VITE_AMPLAR_APPOINTMENT_IFRAME_ALLOWED_ORIGINS` (amplar) and
 * `VITE_MY_HEALTH_APPOINTMENT_IFRAME_ALLOWED_ORIGINS` (myhealth) — each a
 * comma-separated list of origins, e.g.:
 *   https://bookings.example.com,https://staging.example.com
 *
 * Trailing `/*` (CSP-style) and trailing slashes are stripped so the values
 * can be compared directly against `new URL(src).origin` / `event.origin`.
 */
export const getAppointmentIframeAllowedOrigins = () => {
  const amplarOrigins =
    import.meta.env.VITE_AMPLAR_APPOINTMENT_IFRAME_ALLOWED_ORIGINS ?? '';
  const myHealthOrigins =
    import.meta.env.VITE_MY_HEALTH_APPOINTMENT_IFRAME_ALLOWED_ORIGINS ?? '';

  const parseAllowedOrigins = (origins: string) =>
    origins
      .split(',')
      .map((origin) => origin.trim())
      .filter(Boolean)
      .map((origin) => origin.replace(/\/\*$/, '').replace(/\/$/, '')); // strip trailing /* and /

  return {
    myhealth: parseAllowedOrigins(myHealthOrigins),
    amplar: parseAllowedOrigins(amplarOrigins),
  };
};

/**
 * Flattened view of {@link getAppointmentIframeAllowedOrigins} for callers that
 * only need to validate an origin/src against the full allow-list, regardless
 * of which system it belongs to.
 */
export const getAllAppointmentIframeAllowedOrigins = (): string[] => {
  const { myhealth, amplar } = getAppointmentIframeAllowedOrigins();
  return [...myhealth, ...amplar];
};
